/**
 * State holder for the Apprise "Create a virtual machine" section of the New Deployment
 * wizard (step 3). Owns the editable config, the live Azure catalogs the form depends on,
 * re-derives untouched defaults when env / region / VM name / size / options change, and
 * exposes validation + the contract payload.
 *
 * The custom admin password lives ONLY in this hook's component state (and a ref mirror
 * that is blanked on unmount): never in the zustand store, browser storage, URLs, the
 * payload or the review text. It is handed out once, for the write-only top-level
 * `admin_password` field, and cleared after a successful submit.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useAppriseVmOptions } from '@/hooks/useProducts'
import { useAzureCatalog, useAzureSubscription, useSelectedImage, useSelectedSize } from '@/hooks/useAppriseAzure'
import { formatApiError } from '@/utils/format'
import type {
  AppriseAvailability, AppriseBackupPolicy, AppriseBootDiagnostics, AppriseCredentialMode, AppriseLoadBalancer, AppriseNetwork,
  AppriseOperations, AppriseSecurity, AppriseServer, AppriseStorage, AppriseVmConfig, AppriseVmOptions, ApprisePublicIp, AzureImage,
  AzureAvailabilityOption, AzureLocation, AzureResourceGroup, AzureSecurityTypeOption, AzureSubscriptionInfo, AzureVmSize, SizeResources, VmSizing,
} from '@/types'
import { locationCodeFor, restrictLocations, subscriptionBlocker, type CatalogResult } from './appriseCatalog'
import {
  FALLBACK_APPRISE_OPTIONS, catalogVmSizing, legacyPipelineParams, type AppriseCatalogSnapshot, type AppriseDefaultsContext,
} from './appriseDefaults'
import {
  chooseCredentialMode, chooseImage, initAppriseFormState, patchBackupPolicy, patchNetwork, patchOperations, patchSecurity,
  patchServer, patchStorage, rederiveAppriseFormState, resetRegionalSelections, selectBootStorage, selectExistingNetwork,
  selectLoadBalancer, selectNewNetwork, selectNsg, selectPublicIp, setAvailability, setBootDiagnostics, setLoadBalancer,
  setPublicIp, setTagRows, type AppriseFormState,
} from './appriseState'
import { validateAdminPassword } from './appriseCredentials'
import { validateTagRows, type AppriseTagRow } from './appriseTags'
import { errorCountByTab, type AppriseTabId } from './appriseTabs'
import { checkVmSize, imageSizeProblem } from './appriseSizeCheck'
import { hasAppriseErrors, validateAppriseVm, type AppriseValidationResult } from './appriseValidation'
import { buildAppriseVmPayload } from './appriseVmPayload'

export interface UseAppriseVmFormParams {
  enabled: boolean
  environment: string
  /** Region as stored by the wizard (display name, e.g. "East US 2"). */
  region: string
  sku: string
  resources?: SizeResources
}

export interface AppriseLiveData {
  subscription: { info?: AzureSubscriptionInfo; isLoading: boolean; error: string; blocker: string }
  resourceGroups: CatalogResult<AzureResourceGroup>
  locations: CatalogResult<AzureLocation>
  availability: CatalogResult<AzureAvailabilityOption>
  securityTypes: CatalogResult<AzureSecurityTypeOption>
}

export interface AppriseVmFormApi {
  enabled: boolean
  config: AppriseVmConfig
  options: AppriseVmOptions
  optionsLoading: boolean
  optionsFallback: boolean
  catalog: VmSizing
  /** The live Azure facts of the VM size / image the requester has picked (undefined while loading or unknown). */
  pickedSize: AzureVmSize | undefined
  pickedImage: AzureImage | undefined
  /** True when the size config has no resources.vm and the seeded Small values are shown. */
  catalogFallback: boolean
  region: string
  /** Azure location code for the region ("eastus2"). */
  location: string
  /** Portal environment is a production one. */
  production: boolean
  live: AppriseLiveData
  validation: AppriseValidationResult
  hasErrors: boolean
  tabErrors: Record<AppriseTabId, number>
  setServer: (patch: Partial<AppriseServer>) => void
  setNetwork: (patch: Partial<AppriseNetwork>) => void
  setStorage: (patch: Partial<AppriseStorage>) => void
  setBackupPolicy: (patch: Partial<AppriseBackupPolicy>) => void
  setSecurity: (patch: Partial<AppriseSecurity>) => void
  setOperations: (patch: Partial<AppriseOperations>) => void
  setAvailability: (patch: Partial<AppriseAvailability>) => void
  setPublicIp: (patch: Partial<ApprisePublicIp>) => void
  setLoadBalancer: (patch: Partial<AppriseLoadBalancer>) => void
  setBootDiagnostics: (patch: Partial<AppriseBootDiagnostics>) => void
  /** The user picks an image from the live list (sets the image and the OS it implies). */
  chooseImage: (image: AzureImage) => void
  chooseCredential: (mode: AppriseCredentialMode) => void
  network: {
    useNew: () => void
    useExisting: (name: string, resourceGroup: string, subnets: Array<{ name: string; prefix: string }>) => void
    chooseSubnet: (name: string) => void
    publicIp: (mode: ApprisePublicIp['mode'], existing?: { name: string; resource_group: string }) => void
    nsg: (mode: AppriseNetwork['nsg_mode'], existing?: { name: string; resource_group: string }) => void
    loadBalancer: (poolId: string) => void
    bootStorage: (account: { name: string; resource_group: string } | undefined) => void
  }
  tagRows: AppriseTagRow[]
  setTagRows: (rows: AppriseTagRow[]) => void
  /** Custom password state — component state only; never persisted. */
  password: { value: string; confirm: string }
  setPassword: (value: string) => void
  setPasswordConfirm: (value: string) => void
  /** The write-only `admin_password` for the create request (custom password only), else undefined. */
  getAdminPassword: () => string | undefined
  clearPassword: () => void
  /** Contract-shaped payload for pipeline_config.apprise_vm (never contains a password). */
  buildPayload: () => AppriseVmConfig
  /** create_public_ip / load_balancer_required / data_disk_sizes derived from the Apprise block. */
  legacyParams: () => ReturnType<typeof legacyPipelineParams>
}

function isOk<T>(r: CatalogResult<T>): boolean {
  return !r.isLoading && r.items.length > 0
}

export function useAppriseVmForm(params: UseAppriseVmFormParams): AppriseVmFormApi {
  const { enabled, environment, region, sku, resources } = params
  const { data: fetched, isLoading, isError } = useAppriseVmOptions(enabled)
  const options = fetched ?? FALLBACK_APPRISE_OPTIONS

  const baseCtx: AppriseDefaultsContext = useMemo(
    () => ({ environment, region, sku, resources, options }),
    [environment, region, sku, resources, options],
  )
  const [state, setState] = useState<AppriseFormState>(() => initAppriseFormState(baseCtx))
  const server = state.config.server
  const createNew = server.deployment_mode === 'create_new'

  // ── Live catalogs the form depends on (cached 5 min; shared with the tab components) ──
  const subscriptionQuery = useAzureSubscription(enabled)
  const resourceGroups = useAzureCatalog('resource-groups', {}, enabled)
  const allLocations = useAzureCatalog('locations', {}, enabled)
  // the API already returns only allowed regions; filtering again keeps the dropdown limited even from a stale cache
  const locationItems = useMemo(() => restrictLocations(allLocations.items, options.allowed_regions), [allLocations.items, options.allowed_regions])
  const locations = useMemo(() => ({ ...allLocations, items: locationItems }), [allLocations, locationItems])
  const location = useMemo(() => locationCodeFor(region, locations.items), [region, locations.items])
  const availability = useAzureCatalog('availability-options', { location, size: server.vm_size }, enabled && createNew)
  const securityTypes = useAzureCatalog(
    'security-types',
    { location, size: server.vm_size, publisher: server.image.publisher, offer: server.image.offer, sku: server.image.sku },
    enabled && createNew,
  )

  // existing networks (shared with the Networking tab's list): a new network's default name must not reuse one
  const virtualNetworks = useAzureCatalog('virtual-networks', { location }, enabled && createNew)

  const snapshot: AppriseCatalogSnapshot = useMemo(
    () => ({
      resourceGroups: isOk(resourceGroups) ? resourceGroups.items.map((g) => g.name) : undefined,
      virtualNetworks: isOk(virtualNetworks) ? virtualNetworks.items.map((v) => ({ name: v.name, resource_group: v.resource_group })) : undefined,
      availabilityOptions: isOk(availability) ? availability.items : undefined,
      securityTypes: isOk(securityTypes) ? securityTypes.items : undefined,
    }),
    [resourceGroups, virtualNetworks, availability, securityTypes],
  )
  const ctx: AppriseDefaultsContext = useMemo(() => ({ ...baseCtx, catalog: snapshot }), [baseCtx, snapshot])

  // The region changed: existing regional resources chosen for the old region no longer apply.
  const prevLocation = useRef(location)
  useEffect(() => {
    if (!enabled || prevLocation.current === location) return
    prevLocation.current = location
    setState((s) => resetRegionalSelections(s, ctx))
  }, [enabled, location, ctx])

  // Re-derive untouched defaults whenever the context changes (user edits kept).
  useEffect(() => {
    if (!enabled) return
    setState((s) => rederiveAppriseFormState(s, ctx))
  }, [enabled, ctx])

  // ── Custom password: component state only ──
  const [password, setPasswordState] = useState('')
  const [passwordConfirm, setPasswordConfirmState] = useState('')
  const passwordRef = useRef('')
  useEffect(() => () => { passwordRef.current = '' }, [])
  const setPassword = useCallback((v: string) => { passwordRef.current = v; setPasswordState(v) }, [])
  const setPasswordConfirm = useCallback((v: string) => setPasswordConfirmState(v), [])
  const clearPassword = useCallback(() => { passwordRef.current = ''; setPasswordState(''); setPasswordConfirmState('') }, [])

  const catalog = catalogVmSizing(ctx)
  const payload = useMemo(
    () => buildAppriseVmPayload(state.config, { region, options, catalog, resourceGroups: snapshot.resourceGroups }),
    [state.config, region, options, catalog, snapshot.resourceGroups],
  )

  // What Azure says about the picked VM size (offered / restricted / vCPU quota) and how it differs from the T-shirt size's VM
  const pickedSize = useSelectedSize(location, server.vm_size, enabled && createNew)
  const sizeCheck = useMemo(
    () => checkVmSize(pickedSize.size, region, resources?.vm ?? null),
    [pickedSize.size, region, resources?.vm],
  )
  // ... and whether the picked operating system (Windows Server 2019 / 2022 / 2025, Gen 1 or Gen 2) runs on that size
  const pickedImage = useSelectedImage(location, server.image, enabled && createNew)
  const imageProblem = useMemo(() => imageSizeProblem(pickedImage.image, pickedSize.size), [pickedImage.image, pickedSize.size])

  const subscriptionInfo = subscriptionQuery.data
  const blocker = subscriptionBlocker(subscriptionInfo, options.subscription_pinned)
  const subscriptionError = subscriptionQuery.isError ? formatApiError(subscriptionQuery.error) : ''

  const validation = useMemo<AppriseValidationResult>(() => {
    const base = validateAppriseVm(payload, { options, region, environment, catalog })
    // Tags are validated per editor row (the payload map has no row identity).
    const errors: Record<string, string> = {}
    for (const [k, v] of Object.entries(base.errors)) if (!k.startsWith('operations.tags')) errors[k] = v
    if (payload.server.deployment_mode === 'create_new') {
      Object.assign(errors, validateTagRows(state.tagRows))
      if (payload.security.credential_mode === 'custom_password') {
        const pe = validateAdminPassword(password, passwordConfirm, payload.server.admin_username)
        if (pe.password) errors['security.admin_password'] = pe.password
        if (pe.confirm) errors['security.admin_password_confirm'] = pe.confirm
      }
    }
    if (blocker) errors.subscription = blocker
    const warnings = { ...base.warnings }
    if (payload.server.deployment_mode === 'create_new') {
      if (sizeCheck.error) errors['server.vm_size'] = sizeCheck.error
      else if (sizeCheck.warning) warnings['server.vm_size'] = sizeCheck.warning
      if (imageProblem && !errors['server.image']) errors['server.image'] = imageProblem
    }
    return { errors, warnings }
  }, [payload, options, region, environment, catalog, state.tagRows, password, passwordConfirm, blocker, sizeCheck, imageProblem])

  const mutate = (fn: (s: AppriseFormState) => AppriseFormState) => setState(fn)

  return {
    enabled,
    config: state.config,
    options,
    optionsLoading: enabled && isLoading,
    optionsFallback: enabled && (isError || (!isLoading && !fetched)),
    pickedSize: pickedSize.size,
    pickedImage: pickedImage.image,
    catalog,
    catalogFallback: !resources?.vm,
    region,
    location,
    production: environment === 'production' || environment === 'prod_usgov',
    live: {
      subscription: { info: subscriptionInfo, isLoading: subscriptionQuery.isLoading, error: subscriptionError, blocker },
      resourceGroups, locations, availability, securityTypes,
    },
    validation,
    hasErrors: hasAppriseErrors(validation),
    tabErrors: errorCountByTab(validation.errors),
    setServer: (patch) => mutate((s) => patchServer(s, patch, ctx)),
    setNetwork: (patch) => mutate((s) => patchNetwork(s, patch, ctx)),
    setStorage: (patch) => mutate((s) => patchStorage(s, patch, ctx)),
    setBackupPolicy: (patch) => mutate((s) => patchBackupPolicy(s, patch, ctx)),
    setSecurity: (patch) => mutate((s) => patchSecurity(s, patch, ctx)),
    setOperations: (patch) => mutate((s) => patchOperations(s, patch, ctx)),
    setAvailability: (patch) => mutate((s) => setAvailability(s, patch, ctx)),
    setPublicIp: (patch) => mutate((s) => setPublicIp(s, patch, ctx)),
    setLoadBalancer: (patch) => mutate((s) => setLoadBalancer(s, patch, ctx)),
    setBootDiagnostics: (patch) => mutate((s) => setBootDiagnostics(s, patch, ctx)),
    chooseImage: (image) => mutate((s) => chooseImage(s, image, ctx)),
    chooseCredential: (mode) => {
      if (mode !== 'custom_password') clearPassword()
      mutate((s) => chooseCredentialMode(s, mode, ctx))
    },
    network: {
      useNew: () => mutate((s) => selectNewNetwork(s, ctx)),
      useExisting: (name, resourceGroup, subnets) =>
        mutate((s) => selectExistingNetwork(s, { name, resource_group: resourceGroup, address_prefixes: [], subnets }, undefined, ctx)),
      chooseSubnet: (name) => mutate((s) => patchNetwork(s, { subnet_name: name }, ctx)),
      publicIp: (mode, existing) => mutate((s) => selectPublicIp(s, mode, existing && { ...existing, sku: '', allocation: '' }, ctx)),
      nsg: (mode, existing) => mutate((s) => selectNsg(s, mode, existing, ctx)),
      loadBalancer: (poolId) => mutate((s) => selectLoadBalancer(s, poolId, ctx)),
      bootStorage: (account) => mutate((s) => selectBootStorage(s, account && { ...account, sku: '' }, ctx)),
    },
    tagRows: state.tagRows,
    setTagRows: (rows) => mutate((s) => setTagRows(s, rows)),
    password: { value: password, confirm: passwordConfirm },
    setPassword,
    setPasswordConfirm,
    getAdminPassword: () =>
      payload.security.credential_mode === 'custom_password' && createNew && passwordRef.current ? passwordRef.current : undefined,
    clearPassword,
    buildPayload: () => payload,
    legacyParams: () => legacyPipelineParams(payload),
  }
}
